import { z } from 'zod';

/**
 * §9.1.1 — Kargo formülü ayarları.
 *
 * Kullanıcı bu değerleri elle girer ve girdiği her sayı doğrudan ürün fiyatına
 * yansır. Bu yüzden doğrulama sıkı: negatif ücret, ters bant sınırı veya
 * min > max gibi bir tutarsızlık kaydedilemez.
 */

export const unitTypeSchema = z.enum(['LBS', 'DESI', 'KG']);
export const unitRoundingSchema = z.enum(['NONE', 'CEIL_0_5', 'CEIL_1']);
export const missingDimsActionSchema = z.enum(['USE_FALLBACK', 'BLOCK_PRODUCT', 'USE_DIM_ONLY']);

/** Para alanı: 2 ondalık, negatif olamaz. */
const usd = z
  .number({ error: 'Sayı girin' })
  .min(0, 'Negatif olamaz')
  .max(100_000, 'En fazla 100.000 olabilir');
/** Birim alanı: 3 ondalık. */
const units = z
  .number({ error: 'Sayı girin' })
  .min(0, 'Negatif olamaz')
  .max(100_000, 'En fazla 100.000 olabilir');

export const shippingBandSchema = z
  .object({
    fromUnits: units,
    /** null = üst sınır yok (son bant). */
    toUnits: units.nullable(),
    baseFeeUsd: usd,
    perUnitFeeUsd: usd,
    sortOrder: z.number().int().min(0).default(0),
  })
  .refine((b) => b.toUnits === null || b.toUnits > b.fromUnits, {
    message: 'Bandın bitişi başlangıcından büyük olmalı',
    path: ['toUnits'],
  });

export const shippingFormulaSchema = z
  .object({
    name: z.string().trim().min(1, 'Formüle bir ad verin').max(80),
    warehouseId: z.string().nullable().optional(),
    isDefault: z.boolean().default(false),

    unitType: unitTypeSchema.default('LBS'),
    baseFeeUsd: usd,
    includedUnits: units.default(0),
    perUnitFeeUsd: usd,
    minBillableUnits: units.default(0),
    unitRounding: unitRoundingSchema.default('CEIL_0_5'),

    useDimensionalWeight: z.boolean().default(true),
    // Bölen sıfır olamaz — sıfıra bölme fiyatı sonsuz yapardı
    dimDivisorIn3PerLb: z
      .number({ error: 'Sayı girin' })
      .gt(0, 'Sıfırdan büyük olmalı')
      .max(10_000, 'En fazla 10.000 olabilir')
      .default(139),
    desiDivisorCm3: z
      .number({ error: 'Sayı girin' })
      .gt(0, 'Sıfırdan büyük olmalı')
      .max(1_000_000, 'En fazla 1.000.000 olabilir')
      .default(3000),

    fallbackWeightLbs: units.default(2),
    fallbackAction: missingDimsActionSchema.default('USE_DIM_ONLY'),

    minTotalUsd: usd.nullable().default(null),
    maxTotalUsd: usd.nullable().default(null),
    surchargeUsd: usd.default(0),
    surchargePct: z
      .number({ error: 'Sayı girin' })
      .min(0, 'Negatif olamaz')
      .max(100, 'En fazla 100 olabilir')
      .default(0),

    bands: z.array(shippingBandSchema).max(50).default([]),
  })
  .refine(
    (f) => f.minTotalUsd === null || f.maxTotalUsd === null || f.maxTotalUsd >= f.minTotalUsd,
    { message: 'En yüksek tutar, en düşük tutardan küçük olamaz', path: ['maxTotalUsd'] },
  )
  .refine((f) => !bandsOverlap(f.bands), {
    message: 'Bantlar birbiriyle çakışıyor; aralıkları düzeltin',
    path: ['bands'],
  });

export type ShippingFormulaInput = z.input<typeof shippingFormulaSchema>;
export type ShippingBandInput = z.input<typeof shippingBandSchema>;

/**
 * Bantlar çakışıyor mu?
 *
 * Çakışan bantlar sessiz bir fiyat hatasıdır: aynı ağırlık iki farklı ücrete
 * denk gelir ve hangisinin kazandığı sıralamaya bağlı kalır. Kaydetmeden
 * reddetmek, sonradan yanlış fiyat basmaktan iyidir.
 */
export function bandsOverlap(bands: Array<{ fromUnits: number; toUnits: number | null }>): boolean {
  const sorted = [...bands].sort((a, b) => a.fromUnits - b.fromUnits);
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]!;
    const current = sorted[i]!;
    // Önceki bandın üst sınırı yoksa sonsuza gider — sonrası kesin çakışır
    if (prev.toUnits === null) return true;
    if (current.fromUnits < prev.toUnits) return true;
  }
  return false;
}

/** §9.1.2 — dry-run: kaydetmeden "bu ürün kaça gider?" hesabı. */
export const shippingSimulateSchema = z.object({
  formulaId: z.string().optional(),
  /** Kaydedilmemiş formülü denemek için. */
  formula: shippingFormulaSchema.optional(),
  weightLbs: z.number().min(0).max(2_000).nullable().default(null),
  lengthIn: z.number().min(0).max(1_000).nullable().default(null),
  widthIn: z.number().min(0).max(1_000).nullable().default(null),
  heightIn: z.number().min(0).max(1_000).nullable().default(null),
  lengthCm: z.number().min(0).max(2_500).nullable().default(null),
  widthCm: z.number().min(0).max(2_500).nullable().default(null),
  heightCm: z.number().min(0).max(2_500).nullable().default(null),
});
export type ShippingSimulateInput = z.input<typeof shippingSimulateSchema>;

/** §9.1.3 — ağırlık/boyut kaynağı; UI'da rozet olarak gösterilir. */
export const DIMENSION_SOURCE_LABELS: Record<string, string> = {
  KEEPA_PACKAGE: 'Keepa (paket ölçüsü)',
  KEEPA_ITEM: 'Keepa (ürün ölçüsü)',
  SPAPI_CATALOG: 'Amazon kataloğu',
  SCRAPER: 'Ürün sayfasından okundu',
  MANUAL: 'Elle girildi',
  FALLBACK: 'Varsayılan değer',
};

/** Rozet tonu: elle girilen ve varsayılan değerler dikkat çekmeli. */
export const DIMENSION_SOURCE_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'accent'> = {
  KEEPA_PACKAGE: 'success',
  KEEPA_ITEM: 'success',
  SPAPI_CATALOG: 'accent',
  SCRAPER: 'neutral',
  MANUAL: 'accent',
  FALLBACK: 'warning',
};
